/**
 * HUD chrome: quality, mute, share, achievements, presence.
 */
import {
  ACHIEVEMENT_LABEL,
  ACHIEVEMENT_TOTAL,
  listAchievements,
  noteFirstProbe,
  noteZoneVisit,
  unlock,
  unlockedCount,
  type AchievementId,
} from "./achievements";
import {
  destroyAudio,
  ensureAudio,
  isMuted,
  setMuted,
  syncAudioVisibility,
  zoneStinger,
} from "./audio";
import {
  beatPresence,
  fetchPeering,
  fetchStatusSnippet,
  fetchEdgeHistory,
  postEdgeSample,
} from "./net";
import { shareEdgeCard } from "./share";
import { getEdgeState, setEdgeHistory, subscribeEdge } from "../edge/feed";
import { QUALITY_KEY, writeQuality, type Quality } from "../scene/basis";
import type { SceneApi, ZoneId } from "../scene/types";
import type { Lang } from "../i18n/copy";

let peeringCache: Awaited<ReturnType<typeof fetchPeering>> = null;
let currentLang: Lang = "en";
let boundApi: SceneApi | null = null;

export function setBootLang(lang: Lang): void {
  currentLang = lang;
}

export function getPeering() {
  return peeringCache;
}

export function toast(msg: string): void {
  const el = document.getElementById("toast");
  if (!el) return;
  el.textContent = msg;
  el.hidden = false;
  window.setTimeout(() => {
    el.hidden = true;
  }, 2200);
}

export function refreshAchCount(): void {
  const el = document.getElementById("ach-count");
  if (el) el.textContent = `ach ${unlockedCount()}/${ACHIEVEMENT_TOTAL}`;
}

export function drawMinimap(
  canvas: HTMLCanvasElement,
  state: {
    player: { x: number; z: number };
    yaw: number;
    zones: Array<{ id: string; x: number; z: number; color: number }>;
  },
): void {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const w = canvas.width;
  const h = canvas.height;
  const dark = document.documentElement.dataset.theme !== "light";
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = dark ? "rgba(0,0,0,0.25)" : "rgba(255,255,255,0.2)";
  ctx.fillRect(0, 0, w, h);
  const scale = 1.7;
  const cx = w / 2;
  const cy = h / 2;
  ctx.fillStyle = dark ? "#aaa" : "#333";
  ctx.fillRect(cx - 3, cy - 3, 6, 6);
  for (const z of state.zones) {
    const x = cx + (z.x - state.player.x) * scale;
    const y = cy + (z.z - state.player.z) * scale;
    ctx.fillStyle = "#" + z.color.toString(16).padStart(6, "0");
    ctx.beginPath();
    ctx.arc(x, y, z.id === "edge" ? 5 : 4, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(state.yaw);
  ctx.fillStyle = dark ? "#fff" : "#111";
  ctx.beginPath();
  ctx.moveTo(0, -6);
  ctx.lineTo(4, 5);
  ctx.lineTo(-4, 5);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

export function initFeatureChrome(): () => void {
  const qBtn = document.getElementById("quality-toggle");
  const qInd = document.getElementById("quality-indicator");
  const syncQ = () => {
    const q = localStorage.getItem(QUALITY_KEY) === "low" ? "low" : "high";
    if (qInd) qInd.textContent = q === "low" ? "LQ" : "HQ";
    document.documentElement.dataset.quality = q;
  };
  syncQ();
  const onQuality = () => {
    const cur = (localStorage.getItem(QUALITY_KEY) === "low" ? "low" : "high") as Quality;
    const next: Quality = cur === "low" ? "high" : "low";
    writeQuality(next);
    syncQ();
    boundApi?.setQuality(next);
    toast(next === "low" ? "Quality: Low" : "Quality: High");
  };
  qBtn?.addEventListener("click", onQuality);

  refreshAchCount();
  const muteBtn = document.getElementById("mute-toggle");
  if (muteBtn) {
    const sync = () => {
      muteBtn.classList.toggle("is-on", isMuted());
      muteBtn.title = isMuted() ? "Unmute" : "Mute";
    };
    sync();
    muteBtn.addEventListener("click", () => {
      setMuted(!isMuted());
      sync();
      void ensureAudio();
    });
  }
  document.getElementById("share-toggle")?.addEventListener("click", () => {
    const latest = getEdgeState().samples.at(-1) ?? null;
    void shareEdgeCard(latest);
    toast(currentLang === "zh" ? "已导出卡片" : "Card exported");
  });
  document.addEventListener("doorpost:achievement", ((e: Event) => {
    const id = (e as CustomEvent<{ id: AchievementId }>).detail.id;
    const label = ACHIEVEMENT_LABEL[id];
    if (label) toast((currentLang === "zh" ? label.zh : label.en) + " ✓");
    refreshAchCount();
    boundApi?.setAchievements(listAchievements().filter((a) => a.unlockedAt).map((a) => a.id));
  }) as EventListener);
  document.addEventListener("pointerdown", () => { void ensureAudio(); }, { once: true });
  document.addEventListener("doorpost:egg", ((e: Event) => {
    const id = (e as CustomEvent<{ id: string }>).detail?.id;
    if (!id) return;
    const map: Record<string, AchievementId> = {
      jump_air: "jump_air",
      secret_orbit: "secret_orbit",
      konami: "konami",
      night_owl: "night_owl",
      plaza_sprinter: "plaza_sprinter",
      gate_seal: "gate_seal",
    };
    const ach = map[id];
    if (ach) unlock(ach);
  }) as EventListener);
  document.addEventListener("doorpost:toast", ((e: Event) => {
    const msg = (e as CustomEvent<{ msg: string }>).detail?.msg;
    if (msg) toast(msg);
  }) as EventListener);
  void fetchPeering().then((p) => { peeringCache = p; });
  void fetchStatusSnippet().then((st) => {
    const chip = document.getElementById("status-chip");
    if (chip && st) chip.textContent = "status " + st;
  });
  void beatPresence(null).then((n) => {
    const el = document.getElementById("online-count");
    if (el) el.textContent = "online " + n;
  });

  let lastPostedSample = "";
  const unsubscribeEdge = subscribeEdge((state) => {
    const latest = state.samples.at(-1);
    if (!latest) return;
    const sampleKey = `${latest.t}|${latest.colo ?? ""}|${latest.rttMs}`;
    if (sampleKey === lastPostedSample) return;
    lastPostedSample = sampleKey;
    noteFirstProbe();
    void postEdgeSample({
      colo: latest.colo,
      rttMs: latest.rttMs,
      asn: latest.asn,
      country: latest.country,
    });
  });
  document.addEventListener("visibilitychange", syncAudioVisibility);

  return () => {
    qBtn?.removeEventListener("click", onQuality);
    unsubscribeEdge();
    document.removeEventListener("visibilitychange", syncAudioVisibility);
    destroyAudio();
  };
}

export function bindFeatureApi(api: SceneApi): void {
  boundApi = api;
  pushSceneMeta(api);
}

export function unbindFeatureApi(api: SceneApi): void {
  if (boundApi === api) boundApi = null;
}

export function onZoneActivated(id: ZoneId): void {
  if (!id) return;
  noteZoneVisit(id);
  refreshAchCount();
  zoneStinger(id === "edge" ? 620 : 480);
  void beatPresence(id).then((n) => {
    const el = document.getElementById("online-count");
    if (el) el.textContent = "online " + n;
  });
  boundApi?.setAchievements(listAchievements().filter((a) => a.unlockedAt).map((a) => a.id));
}

export function enrichAsBlurb(base: string, id: string): string {
  if (id !== "as133555" || !peeringCache) return base;
  const extra = [
    peeringCache.name,
    peeringCache.info_prefixes4 != null ? "v4 " + peeringCache.info_prefixes4 : null,
    peeringCache.info_prefixes6 != null ? "v6 " + peeringCache.info_prefixes6 : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return extra ? base + " — " + extra : base;
}

export function pushSceneMeta(api: SceneApi): void {
  api.setAchievements(listAchievements().filter((a) => a.unlockedAt).map((a) => a.id));
  void fetchEdgeHistory().then((items) => {
    setEdgeHistory(items);
    api.setEdgeHistory(items);
  });
}

export function wireMinimap(api: SceneApi): () => void {
  const mm = document.querySelector<HTMLCanvasElement>("#minimap");
  if (!mm) return () => {};

  const intervalMs = 250;
  let timer = 0;
  let disposed = false;

  const paint = () => {
    if (document.hidden || !mm.isConnected || mm.getClientRects().length === 0) return;
    drawMinimap(mm, api.getMinimapState());
  };
  const schedule = () => {
    if (disposed || document.hidden || timer) return;
    timer = window.setTimeout(tick, intervalMs);
  };
  const tick = () => {
    timer = 0;
    if (disposed) return;
    paint();
    schedule();
  };
  const onVisibilityChange = () => {
    if (document.hidden) {
      window.clearTimeout(timer);
      timer = 0;
      return;
    }
    paint();
    schedule();
  };

  document.addEventListener("visibilitychange", onVisibilityChange);
  paint();
  schedule();

  return () => {
    if (disposed) return;
    disposed = true;
    window.clearTimeout(timer);
    timer = 0;
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };
}
